import Config from '@config';
import { enumLabel } from '@gasbi/shared';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import i18n from 'i18next';

import { injectHouseholdsSlice } from '@/app/householdsApi';
import { transformGasConsumingEquipment } from '@/features/gasConsumingEquipments';
import { SealFull, transformSeal } from '@/features/meteringSeals';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions, infiniteQueryOptionsFn } from '@/features/utils';

import './__mocks__';
import {
  GasMeteringDevice,
  GasMeteringDeviceRequestBody,
  GasMeteringNode,
  GasMeteringNodeDevice,
  GasMeteringNodeEquipment,
  GasMeteringNodeGrid,
  GasMeteringNodeRequestBody,
  MeteringPoint,
  MeteringPointDocument,
  MeteringPointDocumentTypeOption,
  MeteringPointGrid,
  MeteringPointHeatedAreaRequestBody,
  MeteringPointHotWaterSupplyRequestBody,
  MeteringPointOwnerRequestBody,
  MeteringPointPersonsRequestBody,
  MeteringPointRequestBody,
  MeteringPointTechnicalParametersRequestBody,
} from './types/types';

const transformMeteringDevice = (device: GasMeteringNodeDevice) => ({
  ...device,
  name:
    device?.name ||
    [
      enumLabel(i18n.t, 'MeteringDeviceGroup', device.meteringDeviceGroup),
      device.meteringDeviceTypeName,
      device.meterThroughputName,
      device.factoryNumber,
    ]
      .filter(Boolean)
      .join(' '),
});

export type UploadMeteringPointDocumentPayload = {
  meteringPointId: string;
  documentTypeId: string;
  documents: File[];
};

export const meteringPointsApi = injectHouseholdsSlice({
  baseUrl: `${Config.VITE_API_URL}/metering-points`,
  tagTypes: [
    'MeteringPoint',
    'MeteringPoints',
    'GasMeteringNodes',
    'GasMeteringNode',
    'MeteringPointDevice',
    'MeteringPointDevices',
    'MeteringPointDocuments',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getMeteringPointsI: builder.infiniteQuery<
      MeteringPointGrid[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringPoints'],
    }),
    getMeteringPointById: builder.query<MeteringPoint, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MeteringPoint'],
    }),
    getMeteringPointDocumentsI: builder.infiniteQuery<
      MeteringPointDocument[],
      FiltersGrid & { meteringPointId: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/${queryArg.meteringPointId}/documents/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: ['MeteringPointDocuments'],
    }),
    getMeteringPointDocumentTypes: builder.query<
      MeteringPointDocumentTypeOption[],
      void
    >({
      query: () => ({ url: '/document-types' }),
    }),
    uploadMeteringPointDocument: builder.mutation<
      void,
      UploadMeteringPointDocumentPayload
    >({
      query: ({ meteringPointId, documentTypeId, documents }) => {
        const formData = new FormData();
        formData.append('documentTypeId', documentTypeId);
        documents.forEach(doc => formData.append('documents', doc, doc.name));
        return {
          url: `/${meteringPointId}/documents`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['MeteringPointDocuments'],
    }),
    createMeteringPoint: builder.mutation<
      MeteringPoint,
      MeteringPointRequestBody
    >({
      query: data => ({
        url: '',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'MeteringPoints',
        { type: 'MeteringPoint', id: result?.id },
      ],
    }),
    // TODO: only for mock Select options data - remove after real data will be available
    getMockSelectData: builder.infiniteQuery<
      { id: string; name: string },
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 100 }),
      query: ({ queryArg, pageParam }) => ({
        url: `/mock-data`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringPoints'],
    }),
    // TODO: only for mock Select options data - remove after real data will be available
    getAddressMockDateI: builder.infiniteQuery<
      { id: string; name: string },
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions: infiniteQueryOptionsFn({ pageSize: 100 }),
      query: ({ queryArg, pageParam }) => ({
        url: `/address`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringPoints'],
    }),
    updateMeteringPointOwner: builder.mutation<
      string,
      MeteringPointOwnerRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/owner`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
    patchMeteringPointOwner: builder.mutation<
      string,
      MeteringPointOwnerRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/owner`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
    updateMeteringPointHotWaterSupply: builder.mutation<
      string,
      MeteringPointHotWaterSupplyRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/hot-water-supply`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
    updateMeteringPointHeatedArea: builder.mutation<
      string,
      MeteringPointHeatedAreaRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/heated-area`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
    updateMeteringPointPersons: builder.mutation<
      string,
      MeteringPointPersonsRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/persons`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
    updateMeteringPointTechnicalParameters: builder.mutation<
      void,
      MeteringPointTechnicalParametersRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/technical-parameters`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
    /** Gas Metering Nodes */
    getMeteringPointGasMeteringNodes: builder.query<GasMeteringNode[], string>({
      query: meteringPointId => ({
        url: `/${meteringPointId}/gas-metering-nodes/query`,
      }),
      providesTags: ['GasMeteringNodes'],
    }),
    getMeteringPointGasMeteringNodeByNodeId: builder.query<
      GasMeteringNodeGrid,
      { meteringPointId: string; meteringNodeId: string }
    >({
      query: ({ meteringPointId, meteringNodeId }) => ({
        url: `/${meteringPointId}/gas-metering-nodes/${meteringNodeId}`,
      }),
      transformResponse: (node: GasMeteringNodeGrid) => ({
        ...node,
        devices: node.devices.map(device => transformMeteringDevice(device)),
        gasConsumptionEquipments: node.gasConsumptionEquipments.map(eq =>
          transformGasConsumingEquipment(eq as any),
        ) as unknown as GasMeteringNodeEquipment[],
        seals: node.seals.map(seal => transformSeal(seal as SealFull)),
      }),
      providesTags: ['GasMeteringNode'],
    }),
    createMeteringPointGasMeteringNode: builder.mutation<
      string,
      GasMeteringNodeRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/gas-metering-nodes`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['GasMeteringNode', 'GasMeteringNodes'],
    }),
    updateMeteringPointGasMeteringNode: builder.mutation<
      string,
      GasMeteringNodeRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/gas-metering-nodes/${data?.id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['GasMeteringNode', 'GasMeteringNodes'],
    }),
    getMeteringPointDevices: builder.query<GasMeteringDevice[], string>({
      query: meteringPointId => ({
        url: `/${meteringPointId}/metering-devices`,
      }),
      providesTags: ['MeteringPointDevices'],
    }),
    getMeteringPointDeviceById: builder.query<
      GasMeteringDevice,
      { meteringPointId: string; meteringDeviceId: string }
    >({
      query: ({ meteringPointId, meteringDeviceId }) => ({
        url: `/${meteringPointId}/metering-devices/${meteringDeviceId}`,
      }),
      transformResponse: (data: GasMeteringDevice) =>
        transformMeteringDevice(data as any) as unknown as GasMeteringDevice,
      providesTags: ['MeteringPointDevice'],
    }),
    createMeteringPointDevice: builder.mutation<
      string,
      GasMeteringDeviceRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/metering-devices`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [
        'MeteringPointDevice',
        'MeteringPointDevices',
        'GasMeteringNode',
      ],
    }),
    updateMeteringPointDevice: builder.mutation<
      string,
      GasMeteringDeviceRequestBody
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/metering-devices/${data?.id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: [
        'MeteringPointDevice',
        'MeteringPointDevices',
        'GasMeteringNode',
      ],
    }),
    deleteMeteringPointDevice: builder.mutation<
      string,
      { meteringPointId: string; id: string }
    >({
      query: data => ({
        url: `/${data?.meteringPointId}/metering-devices/${data?.id}`,
        method: 'DELETE',
        data,
      }),
      invalidatesTags: [
        'MeteringPointDevice',
        'MeteringPointDevices',
        'GasMeteringNode',
      ],
    }),
    updateMeteringPoint: builder.mutation<
      void,
      {
        meteringPointId: string;
        addressId: string;
        postalCode?: string | null;
        buildingType: string;
        corps: string;
        roomNumber: string;
        floorNumber: string;
      }
    >({
      query: data => ({
        url: `/${data.meteringPointId}/address`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['MeteringPoint', 'MeteringPoints'],
    }),
  }),
});

export const {
  useGetMeteringPointByIdQuery,
  useCreateMeteringPointMutation,
  useUpdateMeteringPointOwnerMutation,
  usePatchMeteringPointOwnerMutation,
  useUpdateMeteringPointHotWaterSupplyMutation,
  useUpdateMeteringPointHeatedAreaMutation,
  useUpdateMeteringPointPersonsMutation,
  useUpdateMeteringPointTechnicalParametersMutation,
  useGetMeteringPointGasMeteringNodesQuery,
  useGetMeteringPointGasMeteringNodeByNodeIdQuery,
  useCreateMeteringPointGasMeteringNodeMutation,
  useUpdateMeteringPointGasMeteringNodeMutation,
  useGetMeteringPointDevicesQuery,
  useGetMeteringPointDeviceByIdQuery,
  useCreateMeteringPointDeviceMutation,
  useUpdateMeteringPointDeviceMutation,
  useDeleteMeteringPointDeviceMutation,
  useUpdateMeteringPointMutation,
  useGetMeteringPointDocumentTypesQuery,
  useUploadMeteringPointDocumentMutation,
} = meteringPointsApi;

export const useGetMeteringPointDocumentsInfiniteQuery =
  normalizeInfiniteQueryData(
    meteringPointsApi.useGetMeteringPointDocumentsIInfiniteQuery,
  );

export const useGetMeteringPointsIInfiniteQuery = normalizeInfiniteQueryData(
  meteringPointsApi.useGetMeteringPointsIInfiniteQuery,
);

// TODO: only for mock Select options data - remove after real data will be available
export const useGetMockSelectDataInfiniteQuery = normalizeInfiniteQueryData(
  meteringPointsApi.useGetMockSelectDataInfiniteQuery,
);

// TODO: only for mock Select options data - remove after real data will be available
export const useGetAddressMockDateIInfiniteQuery = normalizeInfiniteQueryData(
  meteringPointsApi.useGetAddressMockDateIInfiniteQuery,
);
